import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Upload, Trash2, Download, RefreshCw, Copy, Loader2, Eye } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CompareSocial } from "@/components/CompareSocial";

const save = (blob, name) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); };

const ToolRow = ({ gid, t, reload }) => {
  const del = () => window.confirm(`Remove ${t.filename} from this guide's tools?`) && api.delete(`/relocation/guides/${gid}/tools/${t.id}`).then(() => { toast.success("Removed"); reload(); }).catch((e) => toast.error(errMsg(e)));
  const demo = () => api.put(`/relocation/guides/${gid}/tools/${t.id}/demo`, { on: !t.demo }).then(() => { toast.success(t.demo ? "Free demo turned off" : "This calculator is now the free demo on the shop page"); reload(); }).catch((e) => toast.error(errMsg(e)));
  const redo = () => api.post(`/relocation/guides/${gid}/tools/${t.id}/convert`).then(reload).catch((e) => toast.error(errMsg(e)));
  const file = () => api.get(`/relocation/guides/${gid}/tools/${t.id}/file`, { responseType: "blob" }).then((r) => save(r.data, t.filename)).catch((e) => toast.error(errMsg(e)));
  const status = t.calc_status === "adapting" ? "Creating the country version…" : t.calc_status === "ready" ? `Calculator ready · ${t.sections.length} sections, ${t.fields} fields` : t.calc_status === "failed" ? `Calculator failed: ${t.calc_error}` : "Building the in-app calculator…";
  return (
    <div className="flex flex-wrap items-center gap-4 py-4 border-b border-[#E2D9CC]" data-testid={`reloc-tool-${t.id}`}>
      <FileSpreadsheet className="w-5 h-5 text-[#C59B27] shrink-0" strokeWidth={1.4} />
      <div className="mr-auto min-w-0">
        <p className="font-display text-lg text-[#3B1B48]">{t.title}</p>
        <p className="text-xs text-[#8C827A] truncate">{t.filename} · {Math.round(t.size / 1024)} KB · sheets: {(t.sheets || []).join(", ")}{t.adapted_from_guide ? ` · country version${t.changed_cells ? ` (${t.changed_cells} cells localised)` : ""}` : ""}</p>
        <p data-testid={`reloc-tool-status-${t.id}`} className={`text-xs mt-1 ${t.calc_status === "failed" ? "text-red-700" : "text-[#1C181D]"}`}>{["converting", "adapting"].includes(t.calc_status) && <Loader2 className="inline w-3 h-3 mr-1 animate-spin" />}{status}</p>
        {t.summary && <p className="text-sm text-[#8C827A] mt-1">{t.summary}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2 shrink-0">
      <Button data-testid={`reloc-tool-demo-${t.id}`} variant={t.demo ? "default" : "outline"} size="sm" disabled={t.calc_status !== "ready"} onClick={demo} className="rounded-none"><Eye className="w-4 h-4 mr-1" />{t.demo ? "Free demo ✓" : "Use as free demo"}</Button>
      <Button data-testid={`reloc-tool-download-${t.id}`} variant="outline" size="sm" onClick={file} className="rounded-none"><Download className="w-4 h-4 mr-1" />Excel</Button>
      <Button data-testid={`reloc-tool-convert-${t.id}`} variant="outline" size="sm" disabled={t.calc_status === "converting"} onClick={redo} className="rounded-none"><RefreshCw className="w-4 h-4 mr-1" />Rebuild calculator</Button>
      <Button data-testid={`reloc-tool-delete-${t.id}`} variant="ghost" size="sm" onClick={del} className="rounded-none text-red-700"><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

const CountryVersions = ({ g, count }) => {
  const [guides, setGuides] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/relocation/guides").then((r) => setGuides(r.data.filter((x) => x.id !== g.id))).catch(() => {}); }, [g.id]);
  if (!count || !guides.length) return null;
  const all = picked.length === guides.length;
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const run = async (mode) => {
    setBusy(true);
    try {
      if (mode === "adapt") {
        const { data } = await api.post(`/relocation/guides/${g.id}/tools/adapt`, { to: picked });
        if (data.queued) toast.success(`Creating ${data.queued} country version${data.queued === 1 ? "" : "s"}. Each takes about a minute; open a guide's Spreadsheet tools tab to follow along.`);
        else toast.message("Nothing new to create: those guides already have these tools");
        if (data.skipped.length) toast.message(`Skipped (already there or 12-tool limit): ${data.skipped.slice(0, 4).join("; ")}${data.skipped.length > 4 ? "…" : ""}`);
      } else {
        let n = 0;
        for (const to of picked) n += (await api.post(`/relocation/guides/${g.id}/tools/copy`, { to })).data.copied;
        toast.success(`Copied ${n} tool${n === 1 ? "" : "s"} as ${n === 1 ? "it is" : "they are"}`);
      }
      setPicked([]);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-[#E2D9CC] p-6 space-y-4" data-testid="reloc-tools-versions">
      <div>
        <p className="font-display text-xl text-[#3B1B48]">Create versions for other guides</p>
        <p className="text-sm text-[#8C827A]">Country versions keep your layout and formulas and swap in each destination's currency, typical costs, fees and agency names. Then each gets its own calculator. Use “copy as-is” for tools that aren't country-specific.</p>
      </div>
      <label className="flex items-center gap-2 text-sm text-[#3B1B48] border-b border-[#E2D9CC] pb-2 cursor-pointer" data-testid="reloc-tools-versions-all">
        <input type="checkbox" checked={all} onChange={() => setPicked(all ? [] : guides.map((x) => x.id))} className="accent-[#3B1B48] w-4 h-4" />Select all ({guides.length})
      </label>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
        {guides.map((x) => (
          <label key={x.id} className="flex items-center gap-2 text-sm cursor-pointer py-1" data-testid={`reloc-tools-versions-guide-${x.id}`}>
            <input type="checkbox" checked={picked.includes(x.id)} onChange={() => toggle(x.id)} className="accent-[#3B1B48] w-4 h-4" />{x.destination}{x.family ? " with Children" : ""}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        <Button data-testid="reloc-tools-adapt-btn" disabled={!picked.length || busy} onClick={() => run("adapt")} className="rounded-none">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Create {count} country version{count === 1 ? "" : "s"} for {picked.length} guide{picked.length === 1 ? "" : "s"}</Button>
        <Button data-testid="reloc-tools-copy-btn" variant="outline" disabled={!picked.length || busy} onClick={() => run("copy")} className="rounded-none"><Copy className="w-4 h-4 mr-2" />Copy as-is</Button>
      </div>
    </div>
  );
};

export const ToolsPost = ({ g }) => {
  const [post, setPost] = useState(null);
  const load = useCallback(() => api.get(`/relocation/guides/${g.id}/tools/instagram`).then((r) => setPost(r.data?.id ? r.data : null)).catch(() => {}), [g.id]);
  useEffect(() => { load(); }, [load]);
  return <CompareSocial c={g} base={`/relocation/guides/${g.id}/tools`} post={post} reload={load} zipName={`${g.destination}-spreadsheet-tools-instagram.zip`}
    blurb="An announcement carousel: a cover, one phone-style calculator screen per tool (worked out from its sample numbers), and a closing slide with the price, plus a ready caption and hashtags." />;
};

export const RelocTools = ({ g, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const input = useRef(null);
  const load = useCallback(() => api.get(`/relocation/guides/${g.id}/tools`).then((r) => setData(r.data)).catch(() => {}), [g.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.tools.some((t) => ["converting", "adapting"].includes(t.calc_status))) return undefined;
    const t = setTimeout(() => { load(); onChanged(); }, 5000);
    return () => clearTimeout(t);
  }, [data, load, onChanged]);

  const upload = async (files) => {
    for (const f of files) {
      setBusy(f.name);
      try {
        await api.post(`/relocation/guides/${g.id}/tools`, f, { headers: { "Content-Type": "application/octet-stream", "x-filename": encodeURIComponent(f.name) } });
      } catch (e) { toast.error(errMsg(e)); }
    }
    setBusy("");
    input.current.value = "";
    load();
    onChanged();
  };
  const zip = () => api.get(`/relocation/guides/${g.id}/files/tools_zip`, { responseType: "blob" }).then((r) => save(r.data, `${g.destination}_Spreadsheet_Tools.zip`)).catch((e) => toast.error(errMsg(e)));
  const [rates, setRates] = useState(false);
  const refreshRates = async () => {
    setRates(true);
    try {
      const { data: d } = await api.post(`/relocation/guides/${g.id}/tools/refresh-rates`);
      if (d.skipped) toast.info(`No exchange rate available for ${g.destination}`);
      else toast.success(`Updated to 1 ${d.rate.code} = ${d.rate.aud_per} AUD (${d.cells} rate cell${d.cells === 1 ? "" : "s"}); downloads repackaged`);
      load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setRates(false); }
  };
  if (!data) return <p className="eyebrow">Loading tools…</p>;

  return (
    <div data-testid="reloc-tools" className="space-y-6">
      <div className="flex flex-wrap items-end gap-4">
        <div className="mr-auto max-w-2xl">
          <p className="eyebrow mb-2">Spreadsheet tools</p>
          <p className="text-sm text-[#1C181D]">Upload the Excel tools that go with this guide. Each one is sold in the <b>Spreadsheet Tools</b> package{data.price_aud ? ` (A$${data.price_aud}, change it in Store listing)` : ""}, included in the toolkit and the complete bundle, and rebuilt as a calculator in the offline toolkit app.</p>
        </div>
        <input ref={input} type="file" multiple accept=".xlsx,.xlsm" className="hidden" data-testid="reloc-tools-input" onChange={(e) => upload([...e.target.files])} />
        <Button data-testid="reloc-tools-upload" disabled={!!busy} onClick={() => input.current.click()} className="rounded-none">{busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading {busy}</> : <><Upload className="w-4 h-4 mr-2" />Upload spreadsheets</>}</Button>
        {data.has_zip && <Button data-testid="reloc-tools-zip" variant="outline" onClick={zip} className="rounded-none"><Download className="w-4 h-4 mr-2" />Tools package (ZIP)</Button>}
        {data.tools.length > 0 && <Button data-testid="reloc-tools-refresh-rates" variant="outline" disabled={rates} onClick={refreshRates} className="rounded-none">{rates ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Update exchange rate</Button>}
      </div>
      <div className="border-t border-[#E2D9CC]">
        {data.tools.length === 0 && <p className="text-sm text-[#8C827A] py-6" data-testid="reloc-tools-empty">No spreadsheets yet. You can pick several files at once.</p>}
        {data.tools.map((t) => <ToolRow key={t.id} gid={g.id} t={t} reload={() => { load(); onChanged(); }} />)}
      </div>
      <CountryVersions g={g} count={data.tools.filter((t) => !t.adapted_from_guide && !t.copied_from).length} />
      {data.tools.some((t) => t.calc_status === "ready") && <p className="text-sm text-[#8C827A]" data-testid="reloc-tools-post-moved">The Spreadsheet Tools Instagram post is now with the other Instagram posts near the top of this page.</p>}
    </div>
  );
};
